/**
 * Supplier deep-links + catalog categories for Parts Lookup.
 *
 * Both GSF and Euro Car Parts look up parts by vehicle registration (VRM),
 * not by free-text part name. Links therefore require a reg.
 *
 * GSF: /parts?reg=… opens the vehicle parts hub (Explore Parts).
 * Euro: /car-parts?registrationNumber=… opens the vehicle parts hub.
 * Category deep-links are NOT used for either site — both strip/ignore the
 * reg query on product-type pages and keep any existing session vehicle
 * (wrong car). Hub URLs are where each site applies the VRM first.
 */

export const PART_CATEGORY_ORDER = [
  'Brakes',
  'Filters',
  'Engine',
  'Cooling',
  'Electrical',
  'Suspension',
  'Clutch & Transmission',
  'Exhaust',
  'Lighting',
  'Body',
  'HVAC',
  'Wipers',
  'Fluids & Oils',
  'Other',
];

const CATEGORY_RULES = [
  ['Brakes', /brake|abs-|caliper|handbrake|parking-brake|brake-disc|brake-drum|brake-pad|brake-shoe/],
  ['Filters', /filter/],
  ['Fluids & Oils', /^(engine-oil|gear-oil|atf|brake-fluid|coolant|adblue|grease|additive)/],
  ['Wipers', /wiper|washer-pump|washer-jet|washer-fluid/],
  ['Lighting', /light|lamp|bulb|headlight|taillight|indicator|fog-lamp|fog-light|led-/],
  ['HVAC', /(?:^|-)ac-|air-conditioning|heater|blower|climate|condenser|evaporator|cabin-filter/],
  ['Exhaust', /exhaust|catalytic|silencer|muffler|dpf|lambda|oxygen-sensor|egt-/],
  ['Cooling', /radiator|coolant|thermostat|water-pump|cooling-fan|fan-clutch|heater-matrix|intercooler/],
  ['Clutch & Transmission', /clutch|gearbox|transmission|flywheel|propshaft|driveshaft|differential|cv-joint|gear-|manual-transmission|automatic-transmission/],
  ['Suspension', /shock|strut|spring|wishbone|control-arm|bush|stabilizer|anti-roll|suspension|ball-joint|tie-rod|track-rod|steering|hub-bearing|wheel-bearing/],
  ['Engine', /engine|piston|camshaft|crankshaft|timing|valve|spark|glow-plug|turbo|injector|fuel-pump|oil-pump|cylinder|gasket|manifold|throttle|egr|intake|exhaust-manifold|cambelt|timing-belt|timing-chain/],
  ['Electrical', /battery|alternator|starter|relay|fuse|wiring|ignition|coil|sensor|ecu|module|switch|horn|solenoid|cable|terminal/],
  ['Body', /door|bumper|wing|bonnet|hood|mirror|grille|panel|fender|windscreen|window|seal|pillar|spoiler|tailgate|boot-lid/],
];

const GSF_HOME = 'https://www.gsfcarparts.com/';
const GSF_PARTS = 'https://www.gsfcarparts.com/parts';
const ECP_HOME = 'https://www.eurocarparts.com/';
const ECP_PARTS = 'https://www.eurocarparts.com/car-parts';

/**
 * Specific GSF product-type paths (from https://www.gsfcarparts.com/sitemap
 * + /parts department trees). First matching rule wins.
 * Paths are relative to /parts.
 */
const GSF_PART_PATH_RULES = [
  // Brakes
  [/brake-pad-sensor|pad-wear/, '/braking/brake-pads-discs/brake-pad-wear-sensor'],
  [/brake-pad|brake-pads/, '/service-parts/brake-pads-discs/brake-pads'],
  [/brake-disc|brake-discs|brake-rotor/, '/service-parts/brake-pads-discs/brake-discs'],
  [/brake-caliper/, '/braking/brake-hydraulics/brake-caliper'],
  [/brake-shoe|handbrake-shoe/, '/braking/brake-shoes-drums/brake-shoes'],
  [/handbrake-cable|parking-brake-cable|brake-cable/, '/braking/brake-cable/brake-cable'],
  [/brake-drum/, '/braking/brake-shoes-drums/brake-drums'],
  [/brake-hose|brake-pipe/, '/braking/brake-hydraulics/brake-hose'],
  [/brake-servo/, '/braking/brake-hydraulics/brake-servo'],
  [/master-cylinder/, '/braking/brake-hydraulics/brake-master-cylinder'],
  [/abs-sensor|abs-ring|abs-/, '/braking/brake-electrical/abs-ring'],
  [/brake-fluid/, '/service-parts/lubricants/brake-fluid'],
  // Filters
  [/oil-filter/, '/service-parts/filters/oil-filter'],
  [/air-filter/, '/service-parts/filters/air-filter'],
  [/cabin-filter|pollen-filter/, '/service-parts/filters/cabin-filter'],
  [/fuel-filter/, '/service-parts/filters/fuel-filter'],
  // Service / ignition
  [/wiper/, '/service-parts/wiper-blades/wiper-blades'],
  [/washer-pump/, '/service-parts/wiper-blades/washer-pump'],
  [/washer-jet/, '/service-parts/wiper-blades/washer-jet'],
  [/spark-plug/, '/service-parts/plugs/spark-plugs'],
  [/glow-plug/, '/service-parts/plugs/glow-plug'],
  [/ignition-coil/, '/service-parts/ignition/ignition-coil'],
  [/ignition-lead/, '/service-parts/ignition/ignition-lead-kit'],
  [/service-kit/, '/service-parts/service-kits'],
  // Transmission
  [/clutch-kit/, '/transmission/clutch/clutch-kit'],
  [/clutch-cable/, '/transmission/clutch/clutch-cable'],
  [/clutch/, '/transmission/clutch/clutch-kit'],
  [/flywheel/, '/transmission/clutch/flywheel'],
  [/cv-joint|cv-boot/, '/transmission/final-drive/cv-joint-kit'],
  [/driveshaft|drive-shaft/, '/transmission/final-drive/driveshaft'],
  [/propshaft/, '/transmission/final-drive/propshaft-complete'],
  [/wheel-bearing|hub-bearing/, '/transmission/wheel-bearings-hubs/wheel-bearing-kit'],
  [/wheel-hub/, '/transmission/wheel-bearings-hubs/wheel-hub'],
  // Suspension / steering
  [/coil-spring/, '/steering-suspension/springs-components/coil-springs'],
  [/shock|damper|strut/, '/steering-suspension/shocks-components/shock-absorber'],
  [/ball-joint/, '/steering-suspension/suspension-parts/ball-joint'],
  [/wishbone|control-arm|suspension-arm/, '/steering-suspension/suspension-parts/suspension-arm'],
  [/bush/, '/steering-suspension/suspension-parts/suspension-arm-bush'],
  [/tie-rod|track-rod/, '/steering-suspension/steering-parts/tie-rod-end'],
  [/power-steering-pump|pas-pump/, '/steering-suspension/steering-racks-pumps/pas-pump'],
  [/steering-rack/, '/steering-suspension/steering-racks-pumps/steering-rack'],
  [/engine-mount/, '/engine-parts/mountings/engine-mount'],
  // Electrical
  [/alternator/, '/electrical/alternator/alternator'],
  [/starter/, '/electrical/starter-system/starter-motor'],
  [/battery|car-batter/, '/electrical/car-batteries/battery'],
  [/bulb|headlight-bulb|lamp-bulb/, '/service-parts/common-bulbs/all-bulbs'],
  [/horn/, '/electrical/signal-systems/horn'],
  [/fuse/, '/electrical/signal-systems/fuse'],
  // Cooling / HVAC
  [/radiator/, '/cooling-heating/cooling-system/radiator'],
  [/thermostat/, '/cooling-heating/cooling-system/thermostat'],
  [/water-pump/, '/cooling-heating/cooling-system/water-pump'],
  [/intercooler/, '/engine-parts/induction/intercooler'],
  [/cooling-fan|radiator-fan/, '/cooling-heating/cooling-system/radiator-fan'],
  // Engine
  [/timing-belt-kit|cambelt-kit/, '/service-parts/timing-belts/timing-belt-kit'],
  [/timing-belt|cambelt/, '/service-parts/timing-belts/timing-belt'],
  [/timing-chain/, '/engine-parts/timing/timing-chain'],
  [/drive-belt|serpentine|aux-belt/, '/engine-parts/auxilary-drive/drive-belt'],
  [/head-gasket/, '/engine-parts/gaskets/head-gasket'],
  [/gasket|seal/, '/engine-parts/gaskets'],
  [/egr/, '/body-exhaust/exhaust-sensor/egr-valve'],
  [/turbo/, '/engine-parts/induction/turbo'],
  [/fuel-injector|injector/, '/fuel-system/fuel-supply/injector'],
  [/fuel-pump/, '/fuel-system/fuel-supply/fuel-pump'],
  [/oil-pump/, '/engine-parts/sump/oil-pump'],
  // Exhaust
  [/catalytic|cat-converter/, '/body-exhaust/exhaust-system/catalytic-converter'],
  [/dpf|diesel-particulate/, '/body-exhaust/exhaust-system/diesel-particulate-filter'],
  [/lambda|oxygen-sensor|o2-sensor/, '/body-exhaust/exhaust-sensor/o2-sensor'],
  [/silencer|muffler|exhaust/, '/body-exhaust/exhaust-system'],
  // Fluids
  [/engine-oil|^oil(?!-filter|-pump|-seal|-cap|-cooler|-level|-pressure|-filler|-sump|-service)/, '/service-parts/lubricants/engine-oil'],
  [/coolant|anti-freeze|antifreeze/, '/service-parts/lubricants/antifreeze-coolant'],
  [/transmission-oil|gear-oil|atf/, '/service-parts/lubricants/transmission-oil'],
  // Body
  [/mirror/, '/body-exhaust/mirrors/mirror-complete'],
  [/bumper/, '/body-exhaust/bumpers-trim/bumper'],
  [/grille/, '/body-exhaust/body-panels/grille'],
  [/bonnet|hood/, '/body-exhaust/body-panels/bonnet'],
  [/wing|fender/, '/body-exhaust/body-panels/wing'],
];

/** Broader GSF category hubs when we only know the Plutoo category. */
const GSF_CATEGORY_PATH = {
  Brakes: '/braking',
  Filters: '/service-parts/filters',
  Engine: '/engine-parts',
  Cooling: '/cooling-heating',
  Electrical: '/electrical',
  Suspension: '/steering-suspension',
  'Clutch & Transmission': '/transmission',
  Exhaust: '/body-exhaust/exhaust-system',
  Lighting: '/lighting',
  Body: '/body-exhaust',
  HVAC: '/cooling-heating',
  Wipers: '/service-parts/wiper-blades/wiper-blades',
  'Fluids & Oils': '/service-parts/lubricants/engine-oil',
};

/**
 * Euro Car Parts product-type paths (from https://www.eurocarparts.com/sitemap).
 * First matching rule wins — keep specific patterns before broad ones.
 */
const ECP_PART_PATH_RULES = [
  // Brakes
  [/brake-pad-sensor|pad-wear/, '/brake-pad-sensor'],
  [/brake-pad|brake-pads/, '/brake-pads'],
  [/brake-disc|brake-discs|brake-rotor/, '/brake-discs'],
  [/brake-caliper/, '/brake-caliper'],
  [/brake-shoe|handbrake-shoe/, '/brake-shoes'],
  [/handbrake-cable|parking-brake-cable/, '/handbrake-cables'],
  [/brake-drum|handbrake-drum/, '/handbrake-drum'],
  [/brake-hose|brake-pipe/, '/brake-hose-and-brake-pipe'],
  [/brake-servo/, '/brake-servo'],
  [/master-cylinder/, '/master-cylinder'],
  [/abs-sensor|abs-/, '/abs-sensor'],
  [/brake-fluid/, '/brake-fluid'],
  // Filters
  [/oil-filter/, '/oil-filter'],
  [/air-filter/, '/air-filter'],
  [/cabin-filter|pollen-filter/, '/pollen-filter'],
  [/fuel-filter/, '/fuel-filters'],
  // Service / ignition
  [/wiper/, '/wiper-blades'],
  [/spark-plug/, '/spark-plugs'],
  [/glow-plug/, '/glow-plugs'],
  [/ignition-coil/, '/ignition-coil'],
  [/ignition-lead/, '/ignition-leads'],
  // Transmission
  [/clutch-kit/, '/clutch-kit'],
  [/clutch-cable/, '/clutch-cable'],
  [/clutch/, '/clutch-kit'],
  [/flywheel/, '/flywheel'],
  [/cv-joint|cv-boot/, '/cv-joint-kits'],
  // Suspension / steering
  [/coil-spring/, '/coil-spring'],
  [/shock|damper|strut/, '/shock-absorber'],
  [/bush/, '/suspension-bushes'],
  [/wheel-bearing|hub-bearing|wheel-hub/, '/wheel-bearing-and-wheel-hub'],
  [/engine-mount/, '/engine-mount'],
  [/power-steering-pump|pas-pump/, '/power-steering-pump'],
  // Electrical
  [/alternator/, '/alternator'],
  [/starter/, '/starter-motor'],
  [/battery|car-batter/, '/car-battery'],
  [/bulb|headlight-bulb|lamp-bulb/, '/car-bulb'],
  // Cooling / HVAC
  [/radiator/, '/car-radiator'],
  [/thermostat/, '/car-thermostat'],
  [/water-pump/, '/water-pump-gasket'],
  [/intercooler/, '/intercooler'],
  // Engine
  [/timing-belt-kit|cambelt-kit/, '/timing-belt-kit'],
  [/timing-belt|cambelt/, '/timing-belt'],
  [/timing-chain/, '/timing-chain'],
  [/drive-belt|serpentine|aux-belt/, '/car-drive-belt'],
  [/gasket|seal/, '/car-gasket-seals'],
  [/egr/, '/egr-valves'],
  [/turbo/, '/turbocharger'],
  [/fuel-injector|injector/, '/fuel-injectors'],
  [/fuel-pump/, '/fuel-pump'],
  [/oil-pump/, '/oil-pump-kit'],
  // Fluids
  [/engine-oil|^oil(?!-filter|-pump|-seal|-cap|-cooler)/, '/engine-oils'],
  [/coolant|anti-freeze|antifreeze/, '/anti-freeze'],
  [/transmission-oil|gear-oil|atf/, '/transmission-oils'],
];

/** Broader Euro category hubs when we only know the Plutoo category. */
const ECP_CATEGORY_PATH = {
  Brakes: '/brakes',
  Filters: '/car-service-parts',
  Engine: '/engine-parts',
  Cooling: '/car-cooling-parts-and-car-heating',
  Electrical: '/car-electrics-and-car-lighting',
  Suspension: '/suspension-and-steering',
  'Clutch & Transmission': '/car-transmission',
  Exhaust: '/car-body-parts-and-car-exhaust',
  Lighting: '/car-bulb',
  Body: '/car-body-parts-and-car-exhaust',
  HVAC: '/car-cooling-parts-and-car-heating',
  Wipers: '/wiper-blades',
  'Fluids & Oils': '/car-lubricants-and-car-fluids',
};

/** Normalise UK reg for supplier VRM lookup (uppercase, no spaces). */
export const normalizeVehicleReg = (reg) =>
  String(reg || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

const partSlug = (part) =>
  (part?.slug || part?.articleNo || part?.articleNumber || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-');

export const getPartCategory = (partOrSlug) => {
  const slug =
    typeof partOrSlug === 'string'
      ? partOrSlug.toLowerCase()
      : partSlug(partOrSlug);
  if (!slug) return 'Other';
  for (const [name, re] of CATEGORY_RULES) {
    if (re.test(slug)) return name;
  }
  return 'Other';
};

const resolveSupplierPath = (slug, partRules, categoryPaths) => {
  if (slug) {
    for (const [re, path] of partRules) {
      if (re.test(slug)) return path;
    }
  }
  const category = getPartCategory(slug);
  return categoryPaths[category] || '';
};

/** Resolve best GSF path for a part (product type → category hub → /parts). */
export const getGsfPartsPath = (partOrSlug) => {
  const slug =
    typeof partOrSlug === 'string'
      ? partOrSlug.toLowerCase()
      : partSlug(partOrSlug);
  return resolveSupplierPath(slug, GSF_PART_PATH_RULES, GSF_CATEGORY_PATH);
};

/** Resolve best Euro Car Parts path (product type → category hub → /car-parts). */
export const getEcpPartsPath = (partOrSlug) => {
  const slug =
    typeof partOrSlug === 'string'
      ? partOrSlug.toLowerCase()
      : partSlug(partOrSlug);
  return resolveSupplierPath(slug, ECP_PART_PATH_RULES, ECP_CATEGORY_PATH);
};

const buildGsfUrl = (reg, _partPath = '') => {
  // Intentionally ignore category deep-links. GSF PLP/category pages strip
  // ?reg= from the URL and keep the existing customer-vehicle cookie, so
  // opening /parts/.../wiper-blades?reg=FP65EPD can still show a previous BMW.
  // /parts?reg=… is where GSF applies the VRM and shows the category grid.
  return `${GSF_PARTS}?reg=${encodeURIComponent(reg)}`;
};

const buildEcpUrl = (reg, _partPath = '') => {
  // Intentionally ignore category deep-links. Euro product-type pages
  // (e.g. /wiper-blades?registrationNumber=…) often keep a previous session
  // vehicle instead of applying the new VRM — same failure mode as GSF.
  // /car-parts?registrationNumber=… applies the reg and shows the category hub.
  return `${ECP_PARTS}?registrationNumber=${encodeURIComponent(reg)}`;
};

/**
 * Build supplier URLs that open the site with the vehicle registration selected.
 * Returns null links when no reg is available (sites cannot look up parts without it).
 */
export const getSupplierLinks = (part, vehicle = null) => {
  const slug = partSlug(part);
  const reg = normalizeVehicleReg(vehicle?.reg);
  const hasReg = reg.length >= 2;
  const category = getPartCategory(slug);
  const gsfPath = getGsfPartsPath(slug);
  const ecpPath = getEcpPartsPath(slug);

  const gsfUrl = hasReg ? buildGsfUrl(reg, gsfPath) : null;
  const ecpUrl = hasReg ? buildEcpUrl(reg, ecpPath) : null;

  return {
    gsf: {
      id: 'gsf',
      label: 'GSF Car Parts',
      shortLabel: 'GSF',
      url: gsfUrl,
      home: GSF_HOME,
      path: '/parts',
      suggestedPath: gsfPath || '/parts',
      enabled: hasReg,
    },
    euro: {
      id: 'euro',
      label: 'Euro Car Parts',
      shortLabel: 'Euro',
      url: ecpUrl,
      home: ECP_HOME,
      path: '/car-parts',
      suggestedPath: ecpPath || '/car-parts',
      enabled: hasReg,
    },
    reg: hasReg ? reg : null,
    slug,
    category,
    requiresReg: !hasReg,
  };
};

/** @deprecated Prefer getSupplierLinks — kept for any older imports. */
export const buildSupplierSearchQuery = (part, vehicle) =>
  normalizeVehicleReg(vehicle?.reg) || '';

/** Group parts for UI; preserves first-seen order within each category. */
export const groupPartsByCategory = (parts = []) => {
  const map = new Map();
  for (const part of parts) {
    const cat = part.category || getPartCategory(part);
    if (!map.has(cat)) map.set(cat, []);
    map.get(cat).push(part);
  }
  return PART_CATEGORY_ORDER.filter((c) => map.has(c)).map((category) => ({
    category,
    parts: map.get(category),
  }));
};

const partsSupplierLinksApi = {
  PART_CATEGORY_ORDER,
  getPartCategory,
  getGsfPartsPath,
  getEcpPartsPath,
  getSupplierLinks,
  buildSupplierSearchQuery,
  groupPartsByCategory,
  normalizeVehicleReg,
};

export default partsSupplierLinksApi;
